import {
  type ChangeEvent,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import { CloseIcon } from "vega-icons";

import { DropdownButton } from "@vega-core/components/Buttons/DropdownButton/DropdownButton";
import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { FormLabel } from "@vega-core/components/Form/Layout/FormLabel";
import { Checkbox } from "@vega-core/components/shadcn/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@vega-core/components/shadcn/popover";
import { Tag, TagVariants } from "@vega-core/components/Tag/Tag";
import { m } from "@vega-core/paraglide/messages";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { isTruthy } from "@vega-core/utils/isTruthy";

import { SearchInput } from "../../Input/SearchInput/SearchInput";

const LoadMoreSentinel = ({
  name,
  onLoadMore,
  isLoadingMore,
}: {
  name: string;
  onLoadMore: () => void;
  isLoadingMore: boolean;
}) => {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const isLoadingMoreRef = useRef(isLoadingMore);
  isLoadingMoreRef.current = isLoadingMore;

  useEffect(() => {
    const sentinel = sentinelRef.current;

    if (!sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isLoadingMoreRef.current) {
          onLoadMore();
        }
      },
      { threshold: 0.1 },
    );

    observer.observe(sentinel);

    return () => observer.disconnect();
  }, [onLoadMore]);

  return (
    <div
      className="h-9 flex items-center justify-center px-3 text-text/secondary body-extra-small-regular"
      data-testid={`${name}-load-more-sentinel`}
      ref={sentinelRef}
    >
      {isLoadingMore ? m.common_loading() : null}
    </div>
  );
};

export enum DisplayMode {
  Badge = "badge",
  Tags = "tags",
}

export type BaseMultiSelectOption = {
  id?: string | null;
  disabled?: boolean;
  label: string;
  value: string;
  content?: ReactNode;
};

export type ThemedMultiselectDropdownProps = {
  contentWrapperClassName?: string;
  options: BaseMultiSelectOption[];
  disabled?: boolean;
  placeholder?: string;
  searchEnabled?: boolean;
  selectedOptions: BaseMultiSelectOption[];
  setSelectedOptions: (items: BaseMultiSelectOption[]) => void;
  name: string;
  emptySearchElement?: ReactNode;
  renderOption?: (option: BaseMultiSelectOption) => ReactNode;
  label?: string;
  labelTag?: ReactNode;
  hideSelectAllOption?: boolean;
  isLoading?: boolean;
  searchThreshold?: number;
  displayMode?: DisplayMode;
  maxVisibleTags?: number;
  onSearchChange?: (value: string) => void;
  searchValue?: string;
  shouldFilter?: boolean;
  onLoadMore?: () => void;
  hasNextPage?: boolean;
  isLoadingMore?: boolean;
};

export const DEFAULT_SEARCH_THRESHOLD = 5;
export const DEFAULT_TAGS_SEARCH_THRESHOLD = 10;

export const ThemedMultiselectDropdown = ({
  contentWrapperClassName,
  disabled,
  emptySearchElement,
  options,
  placeholder,
  searchEnabled,
  selectedOptions,
  setSelectedOptions,
  name,
  renderOption,
  label,
  labelTag,
  hideSelectAllOption = false,
  isLoading = false,
  searchThreshold,
  displayMode = DisplayMode.Badge,
  maxVisibleTags = 2,
  onSearchChange,
  searchValue,
  shouldFilter = true,
  onLoadMore,
  hasNextPage = false,
  isLoadingMore = false,
}: ThemedMultiselectDropdownProps) => {
  const [internalSearchQuery, setInternalSearchQuery] = useState("");
  const [open, setOpen] = useState(false);

  const isControlledSearch = onSearchChange !== undefined;
  const searchQuery = isControlledSearch
    ? (searchValue ?? "")
    : internalSearchQuery;

  const isTagsMode = displayMode === DisplayMode.Tags;
  const isPaginated = onLoadMore !== undefined;

  const effectiveSearchThreshold =
    searchThreshold ??
    (isTagsMode ? DEFAULT_TAGS_SEARCH_THRESHOLD : DEFAULT_SEARCH_THRESHOLD);

  const handleOptionSelect = (optionValue: string) => {
    let newSelectedOptions = [...selectedOptions];

    if (newSelectedOptions.some((opt) => opt.value === optionValue)) {
      newSelectedOptions = newSelectedOptions.filter(
        (opt) => opt.value !== optionValue,
      );
    } else {
      newSelectedOptions.push(
        options.find((opt) => opt.value === optionValue)!,
      );
    }
    setSelectedOptions(newSelectedOptions);
  };

  const handleRemoveTag = (e: React.MouseEvent, optionValue: string) => {
    e.stopPropagation();
    const newSelectedOptions = selectedOptions.filter(
      (opt) => opt.value !== optionValue,
    );
    setSelectedOptions(newSelectedOptions);
  };

  const handleSearchChange = (value: string) => {
    if (isControlledSearch) {
      onSearchChange(value);
    } else {
      setInternalSearchQuery(value);
    }
  };

  const selectedValuesSet = new Set(selectedOptions.map((opt) => opt.value));

  const filteredOptions = shouldFilter
    ? options.filter((option) =>
        option.label.toLowerCase().includes(searchQuery.toLowerCase()),
      )
    : options;

  const shouldShowSearch =
    searchEnabled === true && options.length >= effectiveSearchThreshold;

  const visibleTags =
    maxVisibleTags > 0
      ? selectedOptions.slice(0, maxVisibleTags)
      : selectedOptions;
  const remainingCount =
    maxVisibleTags > 0 ? selectedOptions.length - maxVisibleTags : 0;

  const renderBadgeContent = () => (
    <>
      <div className="flex w-full">{placeholder || "Select"}</div>
      <div>
        {selectedOptions.length > 0 && (
          <span className="flex items-center justify-center h-4.5 min-w-4.5 px-1 py-0.5 ml-2 bg-text/interactive rounded-small">
            <p className="body-small-semibold text-background/primary">
              {selectedOptions.length}
            </p>
          </span>
        )}
      </div>
    </>
  );

  const renderTagsContent = () => {
    if (selectedOptions.length === 0) {
      return (
        <span className="text-text/disabled">{placeholder || "Select"}</span>
      );
    }

    return (
      <div className="flex flex-wrap gap-1 items-center">
        {visibleTags.map((option) => (
          <Tag
            dataTestId={`${name}-tag-${option.value}`}
            key={option.value}
            variant={TagVariants.Default}
          >
            <span className="truncate max-w-24">{option.label}</span>
            <CloseIcon
              className="h-3 w-3 cursor-pointer ml-1 shrink-0"
              onClick={(e) => handleRemoveTag(e, option.value)}
            />
          </Tag>
        ))}
        {remainingCount > 0 && (
          <span className="text-text/secondary body-small-medium">
            +{remainingCount}
          </span>
        )}
      </div>
    );
  };

  const renderTriggerContent = () =>
    isTagsMode ? renderTagsContent() : renderBadgeContent();

  const popoverProps = { onOpenChange: setOpen, open };

  const dropdownClassName = cnTwMerge(
    "min-w-[134px] flex justify-between w-full",
    isTagsMode && "min-h-[42px] h-auto",
  );

  const handleBadgeModeClick = isTagsMode
    ? undefined
    : (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setOpen(!open);
      };

  const content = (
    <Popover {...popoverProps}>
      <PopoverTrigger asChild>
        <div>
          <DropdownButton
            className={dropdownClassName}
            dataTestId={`${name}-dropdown`}
            disabled={disabled}
            isLoading={isLoading}
            onClick={handleBadgeModeClick}
            open={open}
            type="button"
          >
            {isTagsMode ? (
              <div className="flex w-full py-1">{renderTriggerContent()}</div>
            ) : (
              renderTriggerContent()
            )}
          </DropdownButton>
        </div>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className={cnTwMerge(
          "w-[var(--radix-popover-trigger-width)] min-w-[134px] p-0",
          contentWrapperClassName,
        )}
        data-testid="themed-multiselect-popover"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div
          className={cnTwMerge(
            "rounded-default flex flex-col",
            isPaginated ? "h-96" : "max-h-96",
          )}
        >
          {shouldShowSearch ? (
            <SearchInput
              className="p-2 shrink-0"
              data-testid={`${name}-search-input`}
              name={`${name}-themed-multiselect`}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                handleSearchChange(event.currentTarget.value);
              }}
              value={searchQuery}
              withBorder
            />
          ) : null}
          <div className="flex-1 min-h-0 overflow-y-auto scrollbar-thin scrollbar-thumb-text/secondary scrollbar-track-background/component">
            {filteredOptions.length > 0 && (
              <div className="p-2 flex justify-start">
                {!hideSelectAllOption && !isPaginated && (
                  <ThemedButton
                    className="text-text/interactive"
                    dataTestId={`${name}-select-all-button`}
                    onClick={() => {
                      const hasSelectedOptions = selectedOptions.length > 0;

                      if (hasSelectedOptions) {
                        setSelectedOptions([]);
                      } else {
                        setSelectedOptions(filteredOptions);
                      }
                    }}
                    variant="tertiary"
                  >
                    {selectedOptions.length > 0
                      ? m.input_deselect_all()
                      : m.input_select_all()}
                  </ThemedButton>
                )}
              </div>
            )}
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <div
                  className="flex gap-3 items-center py-3 cursor-pointer body-base-medium px-6 hover:bg-background/hover"
                  key={option.value}
                  onClick={() => handleOptionSelect(option.value)}
                >
                  <Checkbox
                    checked={selectedValuesSet.has(option.value)}
                    data-testid={`${name}-option-${option.value}`}
                    onCheckedChange={() => handleOptionSelect(option.value)}
                  />
                  <div className="flex items-center gap-2">
                    {renderOption ? (
                      renderOption(option)
                    ) : (
                      <span className="capitalize">
                        {option.content || option.label}
                      </span>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="p-2 text-text/primary body-base-regular">
                {emptySearchElement || m.common_noResultsFound()}
              </div>
            )}
            {isPaginated && hasNextPage ? (
              <LoadMoreSentinel
                isLoadingMore={isLoadingMore}
                name={name}
                onLoadMore={onLoadMore}
              />
            ) : null}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );

  if (isTruthy(label)) {
    return (
      <div>
        <FormLabel label={label} tag={labelTag} />
        {content}
      </div>
    );
  }

  return content;
};
